import { useState, useTransition, type ReactNode } from "react";
import { useAuth } from "@clerk/react";
import { ApiRequestError, authenticatedApi } from "@/lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@ndd/ui";

interface DeleteSessionDialogProps {
  sessionId: string;
  onDeleted: () => void;
  trigger: ReactNode;
}

/**
 * Confirm-and-delete dialog used by both the scorecard slide-out panel
 * and the standalone scorecard page. The copy + soft-delete semantics
 * are shared between surfaces; only the trigger button styling differs.
 */
export function DeleteSessionDialog({
  sessionId,
  onDeleted,
  trigger,
}: DeleteSessionDialogProps) {
  const { getToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  function handleConfirm() {
    setError(null);
    startTransition(async () => {
      try {
        await authenticatedApi(getToken, `/api/sessions/${sessionId}`, {
          method: "DELETE",
        });
        setOpen(false);
        onDeleted();
      } catch (caught) {
        if (caught instanceof ApiRequestError) {
          const message = (caught.body as { error?: string } | undefined)?.error;
          setError(message ?? "Failed to delete session.");
        } else {
          setError("Something went wrong.");
        }
      }
    });
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this session?</AlertDialogTitle>
          <AlertDialogDescription>
            The analysis will be hidden from the RD&apos;s list and from any chat
            threads that reference it. This can be restored later if needed.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={isPending}>
            {isPending ? "Deleting..." : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
